import React, { ReactNode } from 'react';
import { ControllerStateAndHelpers } from 'downshift';
import { useFloating, autoUpdate, size } from '@floating-ui/react';
import { SelectMain } from './SelectMain';
import {
	DropdownList,
	IDropdownItem,
	IGetItemDisplayValue,
	IGetItemKey,
} from '../DropdownList';

interface ISelectRootProps extends ControllerStateAndHelpers<IDropdownItem> {
	getItemDisplayValue: IGetItemDisplayValue;
	getItemKey: IGetItemKey;
	items: IDropdownItem[];
	innerRef: React.RefObject<HTMLDivElement>;
	disabled?: boolean;
	invalid?: boolean;
	className?: string;
	placeholder?: string;
	ariaLabel?: string;
	placeholderAsLabel?: boolean;
	icon?: ReactNode;
	defaultScrollToNthItem?: number;
	onClick?: () => void;
	withDropdownFloatingPortal?: boolean;
}

export function SelectRoot(props: ISelectRootProps) {
	const {
		getToggleButtonProps,
		getItemDisplayValue,
		getInputProps,
		getMenuProps,
		getItemProps,
		getItemKey,
		highlightedIndex,
		innerRef,
		selectedItem,
		className,
		disabled,
		invalid,
		ariaLabel,
		placeholder,
		isOpen,
		items,
		placeholderAsLabel,
		icon,
		defaultScrollToNthItem,
		withDropdownFloatingPortal,
	} = props;

	const dropdownProps = {
		getItemDisplayValue,
		getMenuProps,
		getItemProps,
		getItemKey,
		items,
	};

	const [floatingElementWidth, setFloatingElementWidth] = React.useState<
		number | null
	>(null);

	const { x, y, strategy, refs } = useFloating({
		placement: 'bottom',
		middleware: [
			size({
				apply({ rects }) {
					setFloatingElementWidth(rects.reference.width);
				},
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	return (
		<div ref={innerRef} className={className}>
			<SelectMain
				getInputProps={getInputProps}
				floatingRefs={refs}
				getToggleButtonProps={getToggleButtonProps}
				getItemDisplayValue={getItemDisplayValue}
				selectedItem={selectedItem}
				disabled={disabled}
				invalid={invalid}
				ariaLabel={ariaLabel}
				placeholder={placeholder}
				isOpen={isOpen}
				placeholderAsLabel={placeholderAsLabel}
				icon={icon}
				wrapSelectForHorizontalScroll={withDropdownFloatingPortal}
			/>

			<DropdownList
				{...dropdownProps}
				isOpen={isOpen}
				highlightedIndex={highlightedIndex}
				selectedItem={selectedItem}
				defaultScrollToNthItem={defaultScrollToNthItem}
				floatingRefs={refs}
				strategy={strategy}
				x={x}
				y={y}
				floatingElementWidth={floatingElementWidth}
				withDropdownFloatingPortal={withDropdownFloatingPortal}
			/>
		</div>
	);
}
